// src/components/admin/scheduling/components/WeekEventHeader.js
//
// Sticky weekly event header shown at the top of the Admin Week view.
// Shows the 7 days of the current week with their Events in Google-Calendar
// dot-style (dot + time + title) — no heavy filled cards.
// Clicking an event → opens Edit Event modal.
// Clicking a date → navigates to Day view.

import React, { useMemo, useRef, useState } from "react";
import { computeEventsForDate } from "../eventUtils";
import "./WeekEventHeader.css";

// Compact format: "1pm", "1:45pm" — matches Month View, shorter than "1:00 PM"
function formatCompactTime(minutes) {
  const h24 = Math.floor(minutes / 60) % 24;
  const m   = minutes % 60;
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12  = h24 % 12 || 12;
  if (m === 0) return `${h12}${ampm}`;
  return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ymd(d) {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function isTodayDate(date) {
  const t = new Date();
  return (
    date.getFullYear() === t.getFullYear() &&
    date.getMonth()    === t.getMonth()    &&
    date.getDate()     === t.getDate()
  );
}

// ─── Popover for "N more" events ─────────────────────────────────────────────

function MorePopover({ events, onClose, onEventClick }) {
  const ref = useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div className="weh-popover" ref={ref} onClick={(e) => e.stopPropagation()}>
      {events.map((evt) => (
        <div
          key={evt.id}
          className="weh-popoverRow"
          onClick={() => { onEventClick(evt); onClose(); }}
          title={evt.title + (evt.notes ? `\n${evt.notes}` : "")}
        >
          <span className="weh-popoverDot" />
          {evt.startTime != null && (
            <span className="weh-popoverTime">{formatCompactTime(evt.startTime)}</span>
          )}
          <span className="weh-popoverTitle">{evt.title}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Single day column ────────────────────────────────────────────────────────

const MAX_VISIBLE = 3;

function WeekDayColumn({ date, dayKey, allEvents, onEventClick, onScrollToDay }) {
  const [showPopover, setShowPopover] = useState(false);
  const key         = ymd(date);
  const isToday     = isTodayDate(date);
  const isWeekend   = date.getDay() === 0 || date.getDay() === 6;
  const dayAbbr     = date.toLocaleDateString("en-US", { weekday: "short" });
  const dayNum      = date.getDate();
  const monthAbbr   = date.toLocaleDateString("en-US", { month: "short" });

  const dayEvents   = useMemo(() => computeEventsForDate(allEvents, key), [allEvents, key]);
  const visible     = dayEvents.slice(0, MAX_VISIBLE);
  const hiddenCount = Math.max(0, dayEvents.length - MAX_VISIBLE);

  return (
    <div className={`weh-dayCol${isWeekend ? " weh-dayCol--weekend" : ""}`}>
      {/* Clickable date header → scrolls to that day's section in Week view */}
      <div
        className={`weh-dateHeader${isToday ? " weh-dateHeader--today" : ""}`}
        onClick={() => onScrollToDay?.(dayKey, date)}
        title={`Scroll to ${monthAbbr} ${dayNum}`}
      >
        <span className="weh-dayAbbr">{dayAbbr}</span>
        <span className={`weh-dayNum${isToday ? " weh-dayNum--today" : ""}`}>{dayNum}</span>
      </div>

      {/* Event list */}
      <div className="weh-eventList">
        {visible.map((evt) => (
          <div
            key={evt.id}
            className={`weh-eventRow${evt.syncStatus === "sync_failed" ? " weh-eventRow--failed" : ""}`}
            onClick={(e) => { e.stopPropagation(); onEventClick?.(evt); }}
            title={evt.title + (evt.notes ? `\n${evt.notes}` : "")}
          >
            <span className={`weh-dot${evt.syncStatus === "sync_failed" ? " weh-dot--failed" : ""}`} />
            {evt.startTime != null && (
              <span className="weh-eventTime">{formatCompactTime(evt.startTime)}</span>
            )}
            <span className="weh-eventTitle">{evt.title}</span>
            {evt.recurrence?.enabled && <span className="weh-eventRecur">↺</span>}
          </div>
        ))}

        {hiddenCount > 0 && (
          <div style={{ position: "relative" }}>
            <button
              className="weh-moreBtn"
              onClick={(e) => { e.stopPropagation(); setShowPopover((p) => !p); }}
            >
              +{hiddenCount} more
            </button>
            {showPopover && (
              <MorePopover
                events={dayEvents}
                onClose={() => setShowPopover(false)}
                onEventClick={onEventClick}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function WeekEventHeader({
  weekDays,       // Array of { date, dayKey } for the 7 days (Sun→Sat)
  allEvents = [],
  onEventClick,   // (event) → opens Edit Event modal
  onScrollToDay,  // (dayKey, date) → scrolls to that day section in Week view
}) {
  if (!weekDays?.length) return null;

  return (
    <div className="weh-root">
      {/* Seven day columns, Sun–Sat — no spacer before or after */}
      <div className="weh-columns">
        {weekDays.map(({ date, dayKey }) => (
          <WeekDayColumn
            key={`${dayKey}-${ymd(date)}`}
            date={date}
            dayKey={dayKey}
            allEvents={allEvents}
            onEventClick={onEventClick}
            onScrollToDay={onScrollToDay}
          />
        ))}
      </div>
    </div>
  );
}
